<div class="dialog-header">
  <div class="header-copy">
    <h2 mat-dialog-title class="dialog-title">
      <mat-icon class="header-icon">event_available</mat-icon>
      {{ editMode ? 'Edit reservation' : 'Book machine' }}
    </h2>
    <p class="dialog-subtitle">
      {{ editMode
        ? 'Update the schedule, assignment, or notes for this reservation.'
        : 'Reserve a machine for a date range. Availability is checked as you go.' }}
    </p>
  </div>
  <button
    mat-icon-button
    type="button"
    class="header-close"
    (click)="cancel()"
    tabindex="-1"
    aria-label="Close dialog"
  >
    <mat-icon>close</mat-icon>
  </button>
</div>

<mat-dialog-content class="dialog-content">
  <form [formGroup]="form" class="reservation-form">
    <section class="form-section">
      <h3 class="section-title">Machine & job</h3>

      <div class="form-field">
        <label class="field-label" for="machine_id">Machine <span class="required">*</span></label>
        <select id="machine_id" class="field-control" formControlName="machine_id">
          <option value="" disabled>Select machine</option>
          <option *ngFor="let m of machines" [value]="m.id">{{ machineLabel(m) }}</option>
        </select>
        <p class="field-hint" *ngIf="selectedMachine">
          {{ machineHint(selectedMachine) }}
        </p>
        <p class="field-error" *ngIf="form.get('machine_id')?.invalid && form.get('machine_id')?.touched">
          Machine is required
        </p>
      </div>

      <div class="form-field">
        <label class="field-label" for="title">Title <span class="required">*</span></label>
        <input
          id="title"
          type="text"
          class="field-control"
          formControlName="title"
          placeholder="e.g. Road excavation — Phase 1"
        />
        <p class="field-error" *ngIf="form.get('title')?.invalid && form.get('title')?.touched">
          Title is required
        </p>
      </div>
    </section>

    <section class="form-section">
      <div class="section-title-row">
        <h3 class="section-title">Schedule</h3>
        <span class="duration-chip" *ngIf="durationLabel">{{ durationLabel }}</span>
      </div>

      <div class="two-col">
        <div class="form-field">
          <label class="field-label" for="start_date">Start date <span class="required">*</span></label>
          <input id="start_date" type="date" class="field-control" formControlName="start_date" />
          <p class="field-error" *ngIf="form.get('start_date')?.invalid && form.get('start_date')?.touched">
            Required
          </p>
        </div>
        <div class="form-field">
          <label class="field-label" for="end_date">End date <span class="required">*</span></label>
          <input
            id="end_date"
            type="date"
            class="field-control"
            formControlName="end_date"
            [attr.min]="form.get('start_date')?.value || null"
          />
          <p class="field-error" *ngIf="form.get('end_date')?.invalid && form.get('end_date')?.touched">
            Required
          </p>
        </div>
      </div>
      <p class="field-error" *ngIf="form.hasError('dateRange') && datesTouched">
        End date must be on or after the start date
      </p>

      <div
        class="availability-panel"
        [class.is-ok]="available === true"
        [class.is-conflict]="available === false"
        [class.is-checking]="checking"
      >
        <div class="availability-copy">
          <mat-icon class="availability-icon" *ngIf="checking">hourglass_empty</mat-icon>
          <mat-icon class="availability-icon" *ngIf="!checking && available === true">check_circle</mat-icon>
          <mat-icon class="availability-icon" *ngIf="!checking && available === false">error</mat-icon>
          <mat-icon class="availability-icon" *ngIf="!checking && available === null">event</mat-icon>
          <div>
            <p class="availability-title">{{ availabilityTitle }}</p>
            <p class="availability-hint">{{ availabilityHint }}</p>
          </div>
        </div>
        <button
          type="button"
          class="btn-check"
          (click)="checkAvailability()"
          [disabled]="!canCheckAvailability || checking"
        >
          {{ checking ? 'Checking…' : (available === null ? 'Check now' : 'Recheck') }}
        </button>
      </div>

      <div *ngIf="conflicts.length" class="conflict-box">
        <p class="conflict-title">Overlapping reservations</p>
        <ul>
          <li *ngFor="let c of conflicts">
            {{ c.title }} — {{ c.start_date | date:'mediumDate' }} to {{ c.end_date | date:'mediumDate' }}
          </li>
        </ul>
      </div>
    </section>

    <section class="form-section">
      <h3 class="section-title">Assignment</h3>
      <div class="two-col">
        <div class="form-field">
          <label class="field-label" for="project_id">Project</label>
          <select id="project_id" class="field-control" formControlName="project_id">
            <option value="">None</option>
            <option *ngFor="let p of projects" [value]="p.id">{{ p.project_name }}</option>
          </select>
        </div>
        <div class="form-field">
          <label class="field-label" for="operator_staff_id">Operator</label>
          <select id="operator_staff_id" class="field-control" formControlName="operator_staff_id">
            <option value="">Unassigned</option>
            <option *ngFor="let s of staff" [value]="s.id">{{ s.full_name }}</option>
          </select>
        </div>
      </div>

      <div class="form-field" *ngIf="editMode">
        <label class="field-label" for="status">Status</label>
        <select id="status" class="field-control" formControlName="status">
          <option *ngFor="let o of statusOptions" [value]="o.value">{{ o.label }}</option>
        </select>
      </div>
    </section>

    <section class="form-section">
      <h3 class="section-title">
        Details <span class="optional">optional</span>
      </h3>
      <div class="two-col">
        <div class="form-field">
          <label class="field-label" for="purpose">Purpose</label>
          <textarea
            id="purpose"
            class="field-control field-textarea"
            rows="2"
            formControlName="purpose"
            placeholder="Why this machine is needed"
          ></textarea>
        </div>
        <div class="form-field">
          <label class="field-label" for="notes">Notes</label>
          <textarea
            id="notes"
            class="field-control field-textarea"
            rows="2"
            formControlName="notes"
            placeholder="Shift notes, site constraints, or other remarks"
          ></textarea>
        </div>
      </div>
    </section>
  </form>
</mat-dialog-content>

<div class="dialog-footer">
  <p class="footer-hint" *ngIf="form.invalid && !saving">Fill the required fields to continue</p>
  <p class="footer-hint" *ngIf="!form.invalid && available === null && !saving && !checking">
    Confirm availability to enable booking
  </p>
  <p class="footer-hint is-warn" *ngIf="!form.invalid && available === false">
    Resolve the scheduling conflict before booking
  </p>
  <div class="footer-actions">
    <button type="button" class="btn-secondary" (click)="cancel()">Cancel</button>
    <button
      type="button"
      class="btn-primary"
      (click)="save()"
      [disabled]="saving || checking || form.invalid || available !== true"
    >
      {{ saving ? 'Saving…' : (editMode ? 'Update' : 'Book') }}
    </button>
  </div>
</div>
